/**
 * O canal de dúvidas e sugestões, do lado de quem escreve.
 *
 * ## Por que substituiu o e-mail do rodapé
 *
 * Dizia "Dúvidas? Entre em contato: ia@iguatemi.com.br". Aquele endereço é a
 * conta de serviço da equipe, não uma caixa de atendimento — quem escrevesse
 * para lá cairia num lugar que ninguém lê como suporte.
 *
 * E-mail some numa caixa de entrada e não tem estado: quem escreveu não sabe
 * se foi lido, se alguém respondeu, ou se já era. Aqui a mensagem tem
 * situação, resposta, e chega de volta pelo mesmo sino que a pessoa já usa.
 *
 * ## Agora é conversa, e não recado
 *
 * A primeira versão guardava uma resposta só. Quem recebia uma resposta que
 * não resolvia não tinha para onde ir: só abrir outro assunto, e o histórico
 * nascia partido em dois. Agora o assunto tem uma linha do tempo — pergunta,
 * resposta, tréplica — e responder numa conversa encerrada a reabre.
 *
 * ## Três telas na mesma janela
 *
 * Escrever, a lista, e a conversa. Moram juntas de propósito: quem abre para
 * perguntar de novo precisa ver que já perguntou — e, muitas vezes, que a
 * resposta já está ali.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, SessionError, goToSignIn } from '../api.js';
import { Icon } from './Icon.jsx';
import { notify } from './Toast.jsx';
import { relativeTime, exactTime } from '../time.js';

const KINDS = [
  {
    id: 'QUESTION',
    label: 'Dúvida',
    hint: 'Algo que você quer saber e a plataforma não respondeu',
  },
  { id: 'SUGGESTION', label: 'Sugestão', hint: 'Uma ideia do que poderia ficar melhor' },
  { id: 'PROBLEM', label: 'Problema', hint: 'Algo que não funcionou como devia' },
];

/**
 * O estado, dito de quem tem a bola — e não do ponto de vista do sistema.
 *
 * "Aberta" não diz nada a quem escreveu: aberta por quem, esperando o quê. O
 * que a pessoa quer saber é se ainda depende dela ou não.
 */
const STATUS = {
  OPEN: {
    label: 'Aguardando a equipe',
    cls: 'pending',
    next: 'A equipe de Inovação e IA responde por aqui, e o aviso chega no sino.',
  },
  ANSWERED: {
    label: 'Respondida',
    cls: 'ok',
    next: 'Se não resolveu, responda abaixo — a conversa volta para a equipe.',
  },
  CLOSED: {
    label: 'Encerrada',
    cls: 'neutral',
    next: 'A equipe deu o assunto por resolvido. Responder abaixo reabre a conversa.',
  },
};

export function FeedbackDialog({ onClose, initialId = null }) {
  const [tab, setTab] = useState('write');
  const [form, setForm] = useState({ kind: 'QUESTION', subject: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mine, setMine] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // O histórico carrega junto: se já houver resposta esperando, ela deve
  // aparecer no instante em que a pessoa abre — e não depois de ela trocar
  // de aba procurando.
  useEffect(() => {
    let alive = true;
    api
      .feedback()
      .then((r) => {
        if (!alive) return;
        setMine(r.feedback);

        // Veio do sino apontando para uma conversa: abre nela. É o que faz o
        // clique na notificação levar à resposta, e não a uma lista onde a
        // pessoa ainda precisa procurar.
        const alvo = r.feedback.find((f) => f.id === initialId);
        if (alvo) {
          setOpenId(alvo.id);
          setTab('mine');
        } else if (initialId || r.feedback.some((f) => f.status === 'ANSWERED')) {
          setTab('mine');
        }
      })
      .catch((err) => {
        if (err instanceof SessionError) goToSignIn();
      });
    return () => {
      alive = false;
    };
  }, [initialId]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const r = await api.sendFeedback({
        ...form,
        subject: form.subject.trim(),
        message: form.message.trim(),
        // De qual tela a pessoa escreveu. Numa mensagem como "isto aqui não
        // abre", sem isto "aqui" é adivinhação.
        page: window.location.pathname,
      });
      setMine((lista) => [r.feedback, ...(lista ?? [])]);
      setForm({ kind: 'QUESTION', subject: '', message: '' });
      setOpenId(r.feedback.id);
      setTab('mine');
      notify('Mensagem enviada. Você recebe a resposta por aqui.');
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setSaving(false);
    }
    return undefined;
  }

  function onReplied(atualizada) {
    setMine((lista) => lista.map((f) => (f.id === atualizada.id ? atualizada : f)));
  }

  const kind = KINDS.find((k) => k.id === form.kind);
  const respondidas = (mine ?? []).filter((f) => f.status === 'ANSWERED').length;
  const aberta = useMemo(() => (mine ?? []).find((f) => f.id === openId), [mine, openId]);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header editor-head">
          <h2 id="feedback-titulo" className="panel-title">
            {aberta ? aberta.subject : 'Dúvidas ou sugestões'}
          </h2>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        {!aberta && (
          <div className="tabs feedback-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'write'}
              className={`tab ${tab === 'write' ? 'is-active' : ''}`}
              onClick={() => setTab('write')}
            >
              Escrever
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'mine'}
              className={`tab ${tab === 'mine' ? 'is-active' : ''}`}
              onClick={() => setTab('mine')}
            >
              Minhas mensagens
              {respondidas > 0 && <span className="tab-badge">{respondidas}</span>}
            </button>
          </div>
        )}

        {aberta ? (
          <Thread item={aberta} onBack={() => setOpenId(null)} onReplied={onReplied} />
        ) : tab === 'write' ? (
          <form className="editor-form feedback-form" onSubmit={submit}>
            <div className="panel-body editor-body">
              <div className="field">
                <span className="field-label">
                  Tipo
                  <span className="field-hint">{kind?.hint}</span>
                </span>
                <div className="kind-picker" role="radiogroup" aria-label="Tipo de mensagem">
                  {KINDS.map((k) => (
                    <button
                      key={k.id}
                      type="button"
                      role="radio"
                      aria-checked={form.kind === k.id}
                      className={`kind-option ${form.kind === k.id ? 'is-active' : ''}`}
                      onClick={() => setForm((f) => ({ ...f, kind: k.id }))}
                    >
                      {k.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <label className="field-label" htmlFor="fb-subject">
                  Assunto
                </label>
                <input
                  id="fb-subject"
                  className="input"
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  required
                  minLength={3}
                  maxLength={160}
                  placeholder="Em poucas palavras"
                />
              </div>

              <div className="field">
                <label className="field-label" htmlFor="fb-message">
                  Mensagem
                  <span className="field-hint">{form.message.length}/4000</span>
                </label>
                <textarea
                  id="fb-message"
                  className="textarea"
                  rows={7}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  required
                  minLength={10}
                  maxLength={4000}
                  placeholder="Conte o que aconteceu, ou o que você gostaria que existisse."
                />
              </div>

              <p className="field-hint">
                A equipe de Inovação e IA lê e responde por aqui — a resposta chega no sino, e você
                pode responder de volta no mesmo assunto.
              </p>

              {error && (
                <p className="error-note" role="alert">
                  {error}
                </p>
              )}
            </div>

            <footer className="panel-actions">
              <button type="button" className="button ghost" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="button primary" disabled={saving}>
                {saving ? 'Enviando…' : 'Enviar'}
              </button>
            </footer>
          </form>
        ) : (
          <div className="panel-body editor-body">
            {!mine && <p className="state-message">Carregando…</p>}

            {mine?.length === 0 && (
              <p className="state-message">
                Você ainda não escreveu nada. Use a aba ao lado — dúvida, sugestão ou problema.
              </p>
            )}

            <div className="thread-list">
              {mine?.map((f) => {
                const s = STATUS[f.status] ?? { label: f.status, cls: 'neutral' };
                const ultima = f.messages?.[f.messages.length - 1];
                return (
                  <button
                    type="button"
                    className="thread-item"
                    key={f.id}
                    onClick={() => setOpenId(f.id)}
                  >
                    <span className="thread-item-head">
                      <span className="feedback-subject">{f.subject}</span>
                      <span className={`status-pill ${s.cls}`}>{s.label}</span>
                    </span>
                    {ultima && (
                      <span className="thread-item-preview">
                        {ultima.role === 'TEAM' ? 'Equipe: ' : 'Você: '}
                        {ultima.body}
                      </span>
                    )}
                    <span className="thread-item-foot">
                      <span className="feedback-when">
                        {relativeTime(f.lastMessageAt ?? f.createdAt)}
                      </span>
                      <span className="feedback-when">
                        {f.messages?.length ?? 1}{' '}
                        {(f.messages?.length ?? 1) === 1 ? 'mensagem' : 'mensagens'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A conversa de um assunto.
 *
 * Sem alinhamento à esquerda e à direita como num aplicativo de mensagem: o
 * mesmo desenho serve à pessoa e ao painel da equipe, e "quem fica de que
 * lado" mudaria conforme quem olha. Quem falou vem escrito, que não depende
 * de ponto de vista.
 */
export function Thread({
  item,
  onBack,
  onReplied,
  onReply,
  readOnly = false,
  // A mesma conversa, lida dos dois lados. "Aguardando a equipe" é o que a
  // PESSOA precisa saber; a equipe precisa ler "esperando você". O texto do
  // estado entra por aqui em vez de o componente supor de que lado está.
  statusCopy = null,
  children = null,
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const s = statusCopy ?? STATUS[item.status] ?? { label: item.status, cls: 'neutral', next: '' };

  async function send(e) {
    e.preventDefault();
    const corpo = text.trim();
    if (!corpo) return;
    setSaving(true);
    setError(null);
    try {
      const r = onReply ? await onReply(corpo) : (await api.replyFeedback(item.id, corpo)).feedback;
      setText('');
      onReplied?.(r);
      notify('Resposta enviada.');
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setSaving(false);
    }
    return undefined;
  }

  return (
    <>
      <div className="panel-body editor-body">
        {onBack && (
          <button type="button" className="thread-back" onClick={onBack}>
            ← Todas as mensagens
          </button>
        )}

        <div className={`thread-state ${s.cls}`}>
          <span className={`status-pill ${s.cls}`}>{s.label}</span>
          <span className="thread-state-next">{s.next}</span>
        </div>

        <div className="thread">
          {(item.messages ?? []).map((m) => (
            <article className={`thread-msg ${m.role === 'TEAM' ? 'is-team' : ''}`} key={m.id}>
              <header className="thread-msg-head">
                <span className="thread-who">
                  {m.role === 'TEAM' ? (
                    <>
                      <Icon name="inbox" tone="info" />
                      Equipe de Inovação e IA
                    </>
                  ) : (
                    (m.authorName ?? m.authorUpn)
                  )}
                </span>
                <time
                  className="feedback-when"
                  dateTime={m.createdAt}
                  title={exactTime(m.createdAt)}
                >
                  {relativeTime(m.createdAt)}
                </time>
              </header>
              <p className="thread-msg-body">{m.body}</p>
            </article>
          ))}
        </div>

        {error && (
          <p className="error-note" role="alert">
            {error}
          </p>
        )}
      </div>

      {!readOnly && (
        <form className="thread-reply" onSubmit={send}>
          <label className="visually-hidden" htmlFor="thread-reply-text">
            Responder
          </label>
          <textarea
            id="thread-reply-text"
            className="textarea"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={4000}
            placeholder={
              item.status === 'CLOSED' ? 'Responder reabre esta conversa…' : 'Escreva sua resposta…'
            }
          />
          <div className="thread-reply-actions">
            {/* Encerrar/reabrir entra por aqui, e só a equipe manda algo. */}
            {children}
            <button type="submit" className="button primary" disabled={saving || !text.trim()}>
              {saving ? 'Enviando…' : 'Responder'}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

export default FeedbackDialog;
